import type { FieldGroup } from './types.js';

export const disabilityOptions = [
  { value: 'none', label: 'None' },
  { value: 'physical', label: 'Physical disability' },
  { value: 'visual', label: 'Visual impairment' },
  { value: 'hearing', label: 'Hearing impairment' },
  { value: 'speech', label: 'Speech disability' },
  { value: 'cognitive', label: 'Cognitive / Intellectual' },
  { value: 'multiple', label: 'Multiple disabilities' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const substanceUseOptions = [
  { value: 'none', label: 'None' },
  { value: 'tobacco_smoking', label: 'Tobacco (smoking)' },
  { value: 'tobacco_chewing', label: 'Tobacco (chewing / gutka)' },
  { value: 'alcohol', label: 'Alcohol' },
  { value: 'cannabis', label: 'Cannabis' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Diet options — matches dating-schema exactly (14 options).
 * Covers Indian dietary diversity including Jain, Sattvic, and halal/kosher.
 */
export const dietOptions = [
  { value: 'vegetarian', label: 'Vegetarian', description: 'No meat, fish, or eggs' },
  {
    value: 'vegan',
    label: 'Vegan',
    description: 'No animal products at all — including dairy & honey',
  },
  { value: 'eggetarian', label: 'Eggetarian', description: 'Vegetarian but eat eggs' },
  {
    value: 'non_vegetarian',
    label: 'Non-Vegetarian',
    description: 'Eat everything including meat & fish',
  },
  { value: 'pescatarian', label: 'Pescatarian', description: 'No meat, but eat fish & seafood' },
  {
    value: 'occasionally_non_veg',
    label: 'Occasionally Non-Veg',
    description: 'Mostly veg, meat on occasion',
  },
  {
    value: 'flexitarian',
    label: 'Flexitarian',
    description: 'Primarily plant-based, flexible about it',
  },
  { value: 'jain', label: 'Jain', description: 'No root vegetables, strict Jain dietary rules' },
  {
    value: 'sattvic',
    label: 'Sattvic',
    description: 'Pure vegetarian — no onion, garlic, or stimulants',
  },
  {
    value: 'no_onion_no_garlic',
    label: 'No onion, no garlic',
    description: 'Vegetarian without onion & garlic',
  },
  { value: 'halal', label: 'Halal', description: 'Only halal-certified food' },
  { value: 'kosher', label: 'Kosher', description: 'Following Jewish dietary laws' },
  { value: 'other', label: 'Other', description: 'A diet not listed here' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const generalHealthOptions = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const healthInsuranceOptions = [
  { value: 'yes_government', label: 'Yes — Government scheme' },
  { value: 'yes_private', label: 'Yes — Private insurance' },
  { value: 'yes_employer', label: 'Yes — Employer provided' },
  { value: 'no', label: 'No insurance' },
  { value: 'ayushman_bharat', label: 'Ayushman Bharat card' },
] as const;

export const physicalActivityOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'few_times_week', label: 'Few times a week' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'never', label: 'Never' },
] as const;

export const sleepQualityOptions = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'average', label: 'Average' },
  { value: 'poor', label: 'Poor' },
  { value: 'irregular', label: 'Irregular' },
] as const;

export const stressLevelOptions = [
  { value: 'very_low', label: 'Very low' },
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
  { value: 'very_high', label: 'Very high' },
] as const;

export const healthLifestyle: FieldGroup = {
  key: 'health_lifestyle',
  label: 'Health & Lifestyle',
  step: 9,
  description: 'Health, diet, activity, and wellbeing.',
  fields: [
    {
      order: 1,
      key: 'disabilityStatus',
      label: 'Disability Status',
      type: 'multi_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...disabilityOptions],
      sensitive: true,
    },
    {
      order: 2,
      key: 'generalHealth',
      label: 'General Health',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...generalHealthOptions],
    },
    {
      order: 3,
      key: 'healthInsurance',
      label: 'Health Insurance',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...healthInsuranceOptions],
    },
    {
      order: 4,
      key: 'diet',
      label: 'Diet',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...dietOptions],
    },
    {
      order: 5,
      key: 'physicalActivity',
      label: 'Physical Activity',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...physicalActivityOptions],
    },
    {
      order: 6,
      key: 'sleepQuality',
      label: 'Sleep Quality',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...sleepQualityOptions],
    },
    {
      order: 7,
      key: 'substanceUse',
      label: 'Substance Use',
      type: 'multi_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...substanceUseOptions],
      sensitive: true,
    },
    {
      order: 8,
      key: 'stressLevel',
      label: 'Stress Level',
      type: 'single_select',
      step: 9,
      isOnboarding: true,
      optional: true,
      options: [...stressLevelOptions],
    },
  ],
};
